import * as React from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  useCurrentAgency,
  useCurrentAgencyDevices,
  useCurrentAgencyUsers,
  useAuth,
  useFormFactor,
} from 'hooks'
import { PageTitleRightActions, SidePanelContent } from 'components'
import {
  ActivationPin,
  EditDevice,
  DeviceStatusBadge,
  resolveDeviceStatus,
} from 'components/devices'
import {
  Table,
  Button,
  ConfirmationModal,
  Typography,
  Box,
  EL600,
  Loader,
} from 'cs-kevlar-react'
import { ColumnDef, MenuClick } from 'cs-kevlar-react/lib/es/table/base'
import {
  CustomerDeviceViewModel,
  CustomerCertificateViewModel,
  WebAdminUser,
} from '../../api'
import { IdentifiableModel } from 'cs-kevlar-react/lib/es/base'
import { AddCircle } from 'cs-kevlar-react/lib/es/icons'
import {
  canViewAgencyPage,
  isAssisting,
  serverDateToIsoDateString,
  matchIdOrDefaultWithBulk,
} from 'utils'
import {
  CustomerDevice,
  IdKeyedObjectShape,
  CustomerUser,
} from 'contexts/base'
import { DeviceActivationTimeSpan } from 'components/devices/DeviceActivationTimeSpan'
import {
  FilterOptions,
  PaginationOptions,
} from 'cs-kevlar-react/lib/es/table/Table'
import qs from 'qs'
import { CertificateHistory } from 'components/CertificateHistory'
import { AsyncPayload } from 'components/base'
import clsx from 'clsx'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { UserDisplayName } from 'components/UserDisplayName'
import { IconButton } from 'cs-kevlar-react'
import { DeviceMediaRecovery } from 'components/devices/DeviceMediaRecovery'
import { usePageViewAudit } from 'hooks/usePageViewAudit'
import { AddMultipleDevices } from 'components/devices/AddMultipleDevices'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    certStatus: {
      marginTop: theme.spacing(8),
    },
    certRevokedStatus: {
      fontWeight: 'bold',
      color: EL600,
      textTransform: 'uppercase',
    },
    iconButton: {
      // since this is hosted in the page title toolbar which has a right gutter
      // and the icon button itself has the same right padding, do a negative margin
      // so the button padding is used instead of the margin
      marginRight: -1 * parseInt(theme.spacing(8)),
      '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
      },
    },
  }),
)

type DevicesProps = {
  customerAgencyId: string
  licensedAgencyId: string
  deviceId?: string
}

type EditingDeviceState = CustomerDevice | null

type ValidMenuKey =
  | 'Edit'
  | 'ReplaceDevice'
  | 'GenerateKey'
  | 'TextKey'
  | 'CopyDeviceDetails'
  | 'CertificateHistory'
  | 'RecoverMedia'
  | 'DeviceLogs'
  | 'NebulaLogs'

const FILTER_OPTIONS: Partial<FilterOptions> = {
  filterable: true,
  disableLiveFiltering: true,
  enableSuggestions: true,
  enableMultipleSearchTerms: true,
}

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  singularAdjective: 'device',
  pluralAdjective: 'devices',
}

const defaultDevice: CustomerDevice = {
  id: 'NEW',
  customerDeviceId: '',
  name: '',
  assignedUserId: '',
  assignedUserName: '',
  customerAgencyId: '',
  licensedAgencyId: '',
  pin: '',
  pinExpiresOn: '',
  pinExpired: false,
  pinUsed: false,
  email: '',
  notes: '',
  replaceConfirmed: false,
  properties: {},
  loggingConfiguration: {
    verboseLoggingEnabled: false,
    enabledTypes: [],
  },
  certificateConfiguration: {
    allowManualCertificateRenewal: false,
    certificateRenewalWindowInSecondsOverride: null,
  },
}

type AssistedAgency = {
  customerAgencyId: string
  licensedAgencyId: string
}

const buildPageTitle = (user: WebAdminUser | null) => {
  if (!user?.licensedAgencyName) return 'Devices'

  return `${user.licensedAgencyName} - Devices`
}

const buildNavigationUrl = (
  device: EditingDeviceState,
  agency: AssistedAgency | null,
) => {
  if (!agency) {
    if (device === null) return `/devices`

    return `/devices/${device.id}`
  }

  if (device === null)
    return `/assist/customer/${agency.customerAgencyId}/agency/${agency.licensedAgencyId}/devices`

  return `/assist/customer/${agency.customerAgencyId}/agency/${agency.licensedAgencyId}/devices/${device.id}`
}

const lookupUser = (
  row: CustomerDevice,
  licensedUserLookup: IdKeyedObjectShape<CustomerUser>,
) => {
  if (!row.assignedUserId) return null

  const licensedUser = licensedUserLookup[row.assignedUserId]

  if (!licensedUser || !licensedUser.userId) return null

  return licensedUser
}

export const DEVICE_USER_QUERY_PARAM = 'associatedUser'
export const DEVICE_USERS_QUERY_PARAM = 'associatedUsers'

type DeviceReplacementState = {
  id: string
  didReplace: boolean
}

type DevicePinInfo = {
  name: string
  pin: string
}

const Devices: React.FunctionComponent = () => {

  const params = useParams<DevicesProps>()
  if(!params) return null

  const navigate = useNavigate()
  const location = useLocation()
  const { user: authUser } = useAuth()
  const classes = useStyles({})
  const formFactor = useFormFactor()

  usePageViewAudit(buildPageTitle(authUser), !!authUser)

  const queryString = qs.parse(location.search, { ignoreQueryPrefix: true })

  const currentLicensedAgencyId =
    params.licensedAgencyId ?? authUser?.licensedAgencyId

  const { state: currentAgencyState } = useCurrentAgency(
    currentLicensedAgencyId,
  )

  const { agencyUsersLookup, licensedUserLookup } = useCurrentAgencyUsers(
    currentLicensedAgencyId,
  )

  const customerAgencyId =
    params.customerAgencyId ??
    currentAgencyState.agency?.data?.customerAgencyId

  const {
    devices,
    deviceLookup,
    loadDevices,
    addDevice,
    addDevices,
    editDevice,
    deleteDevice,
    generateDevicePin,
    sendDevicePin,
    copyDeviceDetails,
    replaceDevice,
    getDeviceCertificateHistory,
  } = useCurrentAgencyDevices(currentLicensedAgencyId)

  const {
    licensedUsers,
    initialize: initializeUsers,
    loadLicensedUsers,
  } = useCurrentAgencyUsers(currentLicensedAgencyId)

  const [
    isConfirmingPinGenerationForId,
    setIsConfirmingPinGenerationForId,
  ] = React.useState<string | null>(null)

  const [isRecoveringMediaForId, setIsRecoveringMediaForId] = React.useState<
    string | null
  >(null)

  const [
    pinGeneratedFor,
    setPinGeneratedFor,
  ] = React.useState<CustomerDeviceViewModel | null>(null)

  const [
    isConfirmingDeviceReplacementForId,
    setIsConfirmingDeviceReplacementForId,
  ] = React.useState<DeviceReplacementState | null>(null)

  const [
    addedDeviceInfo,
    setAddedDeviceInfo,
  ] = React.useState<DevicePinInfo | null>(null)

  const navigateToRoot = () => {
    if (
      authUser === null ||
      !authUser.customerAgencyId ||
      !authUser.licensedAgencyId
    )
      return navigate(buildNavigationUrl(null, null))

    navigate(
      buildNavigationUrl(null, {
        customerAgencyId: authUser.customerAgencyId,
        licensedAgencyId: authUser.licensedAgencyId,
      }),
    )
  }

  const selectDeviceForEditing = (device: CustomerDevice | null) => {
    if (
      authUser === null ||
      !authUser.customerAgencyId ||
      !authUser.licensedAgencyId
    )
      return navigate(buildNavigationUrl(device, null))

    navigate(
      buildNavigationUrl(device, {
        customerAgencyId: authUser.customerAgencyId,
        licensedAgencyId: authUser.licensedAgencyId,
      }),
    )
  }

  const assignedUser =
    queryString && queryString.associatedUser
      ? licensedUsers.find(
          (licensedUser) => licensedUser.userId === queryString.associatedUser,
        )
      : null
      

  const [assignedUsers, setAssignedUsers] = React.useState(() => 
  {
    var users: CustomerUser[] = []

    if (queryString && queryString.associatedUsers)
    {
      var userIds = (queryString.associatedUsers as string).split(',')

      userIds.forEach((id) => {
        var user = licensedUsers.find((licensedUser) => licensedUser.userId === id)

        if (user)
          users.push(user)
      })
    }

    return users
  })
 
  const defaultDeviceWithAssociatedUser =
    queryString && queryString.associatedUser
      ? {
          ...defaultDevice,
          assignedUserId: assignedUser ? assignedUser.id : null,
          assignedUserName: assignedUser ? assignedUser.name : null,
        }
      : {
          ...defaultDevice,
        }

  const defaultBulkDeviceWithAssociatedUser =
    queryString && queryString.associatedUsers
      ? {
          ...defaultDevice,
          id: 'BULK',
          assignedUserId: queryString.associatedUsers as string,
          assignedUserName: queryString.associatedUsers as string,
        }
      : {
          ...defaultDevice,
          id: 'BULK',
        }

  const editingDevice = matchIdOrDefaultWithBulk(
    deviceLookup,
    params.deviceId,
    defaultDeviceWithAssociatedUser,
    defaultBulkDeviceWithAssociatedUser
  )

  const isEditing = editingDevice !== null && editingDevice.id !== 'NEW' && editingDevice.id !== 'BULK'
  const isAdding = editingDevice !== null && editingDevice.id === 'NEW'
  const isBulkAdding = editingDevice !== null && editingDevice.id === 'BULK'

  const clearSidePanelStates = () => {
    selectDeviceForEditing(null)
    setAddedDeviceInfo(null)
    setIsConfirmingDeviceReplacementForId(null)
  }

  const handleMenuClick = (key: string, row: CustomerDevice) => {
    if (key === 'Edit') {
      clearSidePanelStates()

      selectDeviceForEditing(row)
      return
    }
    
    if (key === 'GenerateKey') {
      setIsConfirmingPinGenerationForId(row.id)
      return
    }

    if (key == 'TextKey') {
      sendDevicePin(row.id)
      return
    }

    if (key == 'CopyDeviceDetails') {
      copyDeviceDetails(row)
      return
    }

    if (key == 'CertificateHistory') {
      loadCertHistory(row.id)
      return
    }

    if (key === 'ReplaceDevice') {
      clearSidePanelStates()

      setIsConfirmingDeviceReplacementForId({
        id: row.id,
        didReplace: false,
      })
      return
    }

    if (key === 'RecoverMedia') {
      setIsRecoveringMediaForId(row.id)
      return
    }

    if (key === 'DeviceLogs' && row.customerAgencyId && row.licensedAgencyId) {
      const link = document.createElement('a')
      link.href = `api/diagnostics/logs/customer/${row.customerAgencyId}/device?licensedAgencyId=${row.licensedAgencyId}&customerDeviceId=${row.customerDeviceId}`
      link.target = '_blank'
      link.click()
      return
    }

    if (key === 'NebulaLogs' && row.customerAgencyId && row.licensedAgencyId) {
      const link = document.createElement('a')
      link.href = `api/diagnostics/logs/customer/${row.customerAgencyId}/nebula?licensedAgencyId=${row.licensedAgencyId}&customerDeviceId=${row.customerDeviceId}`
      link.target = '_blank'
      link.click()
      return
    }
  }

  const onDelete = async () => {
    if (!editingDevice || !customerAgencyId) return

    await deleteDevice(editingDevice.id)
    navigateToRoot()
    clearSidePanelStates()
  }

  const onSave = async (device: CustomerDevice) => {
    if (!customerAgencyId || !currentLicensedAgencyId) {
      return
    }

    device.customerAgencyId = customerAgencyId
    device.licensedAgencyId = currentLicensedAgencyId

    const deviceWithDefaults: CustomerDevice = {
      ...device,
      loggingConfiguration:
        device.loggingConfiguration === null
          ? {
              verboseLoggingEnabled: false,
              enabledTypes: [],
            }
          : device.loggingConfiguration,
    }

    await editDevice(deviceWithDefaults)
    navigateToRoot()
    clearSidePanelStates()
  }

  const onAdd = async (device: CustomerDevice) => {
    if (!customerAgencyId || !currentLicensedAgencyId) return

    // this means they already added something and clicked on Close
    if (addedDeviceInfo !== null) {
      clearSidePanelStates()
      navigateToRoot()
      return
    }

    // HACK: Set the device's ID to the agency's ID since it's required
    // and we don't have a way to generate them right now. This ID doesn't actually matter when adding.
    device.id = customerAgencyId
    device.customerDeviceId = customerAgencyId

    device.customerAgencyId = customerAgencyId
    device.licensedAgencyId = currentLicensedAgencyId
    const addedDevice = await addDevice(device)

    if (addedDevice) {
      setAddedDeviceInfo({
        name: addedDevice.name,
        pin: addedDevice.pin,
      })
    }
  }

  const onAddMultipleDevices = async (devices: CustomerDevice[]) => {
    if (customerAgencyId && currentLicensedAgencyId)
    {
      await addDevices(devices, () => {
        clearSidePanelStates()
        navigateToRoot()
      })
    }
  }

  const onConfirmGeneratePin = async () => {
    if (!customerAgencyId) return
    if (!currentLicensedAgencyId) return
    if (!deviceToGeneratePinFor) return

    if (pinGeneratedFor) {
      setIsConfirmingPinGenerationForId(null)
      setPinGeneratedFor(null)
      return
    }

    const result = await generateDevicePin(deviceToGeneratePinFor.id)
    setPinGeneratedFor(result)
  }

  const onCancelGeneratePin = () => {
    setIsConfirmingPinGenerationForId(null)
    setPinGeneratedFor(null)
  }

  const onCancelRecoverMedia = () => {
    setIsRecoveringMediaForId(null)
  }

  const deviceToReplace =
    isConfirmingDeviceReplacementForId !== null
      ? deviceLookup[isConfirmingDeviceReplacementForId?.id as string]
      : null
  const deviceToGeneratePinFor =
    isConfirmingPinGenerationForId !== null
      ? deviceLookup[isConfirmingPinGenerationForId as string]
      : null

  const didJustReplaceDevice = !!isConfirmingDeviceReplacementForId?.didReplace

  const [isLoading, setIsLoading] = React.useState(false)
  const onConfirmReplaceDevice = async (device: CustomerDevice) => {
    if (!customerAgencyId) return
    if (!currentLicensedAgencyId) return
    if (!deviceToReplace) return

    replaceDevice(device, (newDeviceId) => {
      setIsConfirmingDeviceReplacementForId({
        id: newDeviceId,
        didReplace: true,
      })
    })
  }

  React.useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      await initializeUsers()
      await loadLicensedUsers()
      await loadDevices()
      setIsLoading(false)
    }

    loadData()
  }, [])

  const [currentCertHistory, setCurrentCertHistory] = React.useState<
    AsyncPayload<CustomerCertificateViewModel[]>
  >({ isLoading: false, payload: null })

  const loadCertHistory = async (deviceId: string) => {
    setCurrentCertHistory({ isLoading: true, payload: null })
    const certHistory = await getDeviceCertificateHistory(deviceId)
    setCurrentCertHistory({ isLoading: false, payload: certHistory })
  }

  const dismissSidePanel = () => {
    clearSidePanelStates()
  }

  const canViewLogs = authUser?.isSupport ?? false

  const columns: ColumnDef<CustomerDevice, ValidMenuKey>[] = React.useMemo(
    () => [
      {
        label: 'Device Name',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: CustomerDevice) => row.name,
        filterable: true,
        dataProperty: 'name' as keyof CustomerDevice,
      },
      {
        label: formFactor.isPhone ? 'User' : 'Assigned User',
        sortable: true,
        sortByDefault: false,
        sortValueFunc: (row: CustomerDevice) => row.assignedUserName,
        filterable: true,
        filterFunc: (row) => {
          const user = lookupUser(row, licensedUserLookup)
          return [row.assignedUserName, user?.name]
        },
        component: ({ row }) => (
          <UserDisplayName
            user={lookupUser(row, licensedUserLookup)}
            propName="name"
          />
        ),
      },
      {
        label: '',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        align: 'right',
        padding: 'none',
        component: ({ row }) => <DeviceStatusBadge device={row} />,
      },
      {
        label: 'Status',
        sortable: true,
        sortByDefault: false,
        sortValueFunc: (row: CustomerDevice) => {
          const status = resolveDeviceStatus(row)
          return status.description
        },
        filterable: true,
        filterFunc: (row) => resolveDeviceStatus(row).description,
        dataFunc: (row) => resolveDeviceStatus(row).description
      },
      {
        label: '',
        sortable: false,
        sortByDefault: false,
        sortValueFunc: (row: CustomerDevice) =>
          row.pinExpiresOn && !row.pinUsed && !row.pinExpired
            ? serverDateToIsoDateString(row.pinExpiresOn)
            : '',
        filterable: false,
        isHidden: formFactor.isPhone,
        dataProperty: 'pinExpiresOn' as keyof CustomerDevice,
        component: ({ row }) => (
          <DeviceActivationTimeSpan
            pinExpired={row.pinExpired}
            pinExpiresOn={row.pinExpiresOn}
            pinUsed={row.pinUsed}
          />
        ),
      },
      {
        label: '',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        menuGroups: [
          {
            options: [
              {
                label: 'Edit',
                key: 'Edit',
              },
              {
                label: 'Generate Key',
                key: 'GenerateKey',
                disabled: (row: CustomerDevice) => row.pinUsed,
              },
              {
                label: 'Text Key',
                key: 'TextKey',
                disabled: (row: CustomerDevice) => row.pinUsed || !row.assignedUserId
              },
              {
                label: 'Copy Device Details',
                key: 'CopyDeviceDetails',
              },
              {
                label: 'Certificate History',
                key: 'CertificateHistory',
              },
              {
                label: 'Replace Device',
                key: 'ReplaceDevice',
              },
              {
                label: 'Recover Media',
                key: 'RecoverMedia',
                visible:
                  (currentAgencyState.agency?.data?.globalConfiguration
                    ?.allowMediaUpload &&
                    authUser?.isAdmin &&
                    !authUser?.isSupport) ??
                  false,
              },
            ],
          },
          {
            options: [
              {
                label: 'Device Logs',
                key: 'DeviceLogs',
                visible: canViewLogs,
              },
              {
                label: 'Nebula Logs',
                key: 'NebulaLogs',
                visible: canViewLogs,
              },
            ],
          },
        ],
      },
    ],
    [canViewLogs, agencyUsersLookup, licensedUserLookup, formFactor.isPhone],
  )

  return (
    <React.Fragment>
      <PageTitleRightActions>
        {!formFactor.isPhone && (
          <Button
            onClick={() => {
              clearSidePanelStates()

              selectDeviceForEditing(defaultDevice)
            }}
            color="primary"
          >
            Add Device
          </Button>
        )}
        {formFactor.isPhone && (
          <IconButton
            onClick={() => {
              clearSidePanelStates()

              selectDeviceForEditing(defaultDevice)
            }}
            color="primary"
            className={classes.iconButton}
            size="large">
            <AddCircle />
          </IconButton>
        )}
      </PageTitleRightActions>
      <Table
        data={devices}
        filterOptions={FILTER_OPTIONS}
        paginationOptions={PAGINATION_OPTIONS}
        columns={columns as ColumnDef<CustomerDevice, ValidMenuKey>[]}
        ariaLabel="Devices for Licensed Agency"
        useDefaultRow
        handleMenuClick={
          handleMenuClick as MenuClick<IdentifiableModel, ValidMenuKey>
        }
        selectedId={editingDevice?.id}
        isLoading={isLoading}
        uniqueIdentifier="DevicesList"
      />
      {(isEditing || isAdding) && (
        <SidePanelContent
          anchor="right"
          title={isAdding ? 'Add Device' : `Edit Device`}
          onClose={dismissSidePanel}
        >
          <EditDevice
            reinitializeForm={!!assignedUser}
            canDelete={isEditing}
            key={(editingDevice as CustomerDevice).id}
            device={editingDevice as CustomerDevice}
            onClose={dismissSidePanel}
            onSave={onSave}
            onAdd={onAdd}
            onDelete={onDelete}
            didActionComplete={addedDeviceInfo !== null}
            users={licensedUsers}
            saveText={addedDeviceInfo ? 'Close' : isAdding ? 'Add' : 'Save'}
            canEditDeviceCertificate={authUser?.isSupport ?? false}
            canEditDeviceLogging={authUser?.isSupport ?? false}
            agencyUsersLookup={agencyUsersLookup}
            licensedAgencyId={currentLicensedAgencyId}
          >
            {() => {
              if (!addedDeviceInfo) return null

              return (
                <ActivationPin
                  deviceName={addedDeviceInfo.name}
                  devicePin={addedDeviceInfo.pin}
                />
              )
            }}
          </EditDevice>
        </SidePanelContent>
      )}
      {(isBulkAdding && customerAgencyId && currentLicensedAgencyId) && (
        <SidePanelContent
          anchor="right"
          title={'Add Multiple Devices'}
          onClose={dismissSidePanel}
        >
          <AddMultipleDevices
            customerAgencyId={customerAgencyId}
            licensedAgencyId={currentLicensedAgencyId}
            onAdd={(devices) => onAddMultipleDevices(devices)}
            onClose={dismissSidePanel}
            users={assignedUsers}
          />
        </SidePanelContent>
      )}
      {deviceToReplace && deviceToReplace !== null && (
        <SidePanelContent
          anchor="right"
          title="Replace Device"
          onClose={dismissSidePanel}
        >
          <EditDevice
            canDelete={false}
            key={(deviceToReplace as CustomerDevice).id}
            device={deviceToReplace as CustomerDevice}
            onClose={dismissSidePanel}
            onSave={onConfirmReplaceDevice}
            users={licensedUsers}
            didActionComplete={didJustReplaceDevice}
            saveText={didJustReplaceDevice ? 'Close' : 'Replace Device'}
            isReplacingDevice
            canEditDeviceCertificate={false}
            canEditDeviceLogging={false}
            agencyUsersLookup={agencyUsersLookup}
            licensedAgencyId={currentLicensedAgencyId}
          >
            {() => {
              if (!didJustReplaceDevice || !deviceToReplace) return null

              return (
                <ActivationPin
                  deviceName={deviceToReplace.name}
                  devicePin={deviceToReplace.pin}
                />
              )
            }}
          </EditDevice>
        </SidePanelContent>
      )}
      <ConfirmationModal
        onCancel={onCancelGeneratePin}
        onConfirm={onConfirmGeneratePin}
        open={isConfirmingPinGenerationForId !== null}
        confirmButtonColor="primary"
        confirmText={pinGeneratedFor ? 'Close' : 'Generate Key'}
        showCancelButton={!pinGeneratedFor}
        title="Generate Activation Key"
        message={
          pinGeneratedFor ? (
            <React.Fragment>
              <Typography>
                Activation key for {pinGeneratedFor.name}:{' '}
                <strong>{pinGeneratedFor.pin}</strong>
              </Typography>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Typography>
                Generate new activation key for{' '}
                <strong>{deviceToGeneratePinFor?.name}</strong>
              </Typography>
            </React.Fragment>
          )
        }
      />
      <ConfirmationModal
        onConfirm={() =>
          setCurrentCertHistory({ isLoading: false, payload: null })
        }
        onCancel={() =>
          setCurrentCertHistory({ isLoading: false, payload: null })
        }
        open={
          currentCertHistory.payload !== null || currentCertHistory.isLoading
        }
        confirmText="Close"
        showCancelButton={false}
        title="Certificate History"
        message={
          <Box>
            {currentCertHistory.isLoading && (
              <Loader size="sm" text="Loading device certificate information" />
            )}
            {!currentCertHistory.isLoading &&
              currentCertHistory.payload?.map(
                (cert: CustomerCertificateViewModel, idx: number) => (
                  <CertificateHistory
                    certificate={cert}
                    className={clsx(idx > 0 && classes.certStatus)}
                    key={`CertHistory::${cert.customerCertificateId}`}
                  />
                ),
              )}
          </Box>
        }
      />

      <DeviceMediaRecovery
        deviceId={isRecoveringMediaForId}
        isRecoveringMedia={isRecoveringMediaForId !== null}
        onCancel={onCancelRecoverMedia}
      />
    </React.Fragment>
  );
}

export const DevicesPage: PageDefinition = {
  routes: (user) => {
    if (!isAssisting(user)) {
      return [
        '/devices/',
        '/devices/:deviceId'
      ]
    } 

    return [
      '/assist/customer/:customerAgencyId/agency/:licensedAgencyId/devices/',
      '/assist/customer/:customerAgencyId/agency/:licensedAgencyId/devices/:deviceId',
    ]
  },
  type: [PageType.Agency],
  sortOrder: () => 1,
  component: <Devices />,
  navLinkText: 'Devices',
  pageTitle: buildPageTitle,
  canAccess: canViewAgencyPage,
  buildNavLink: (user) => {
    if (!isAssisting(user)) return '/devices'

    return buildNavigationUrl(null, {
      customerAgencyId: user?.customerAgencyId as string,
      licensedAgencyId: user?.licensedAgencyId as string,
    })
  },
}
